// src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebaseConfig';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, getDocs, collection, query, where, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import {
  FaBars,
  FaTachometerAlt,
  FaFileInvoice,
  FaUsers,
  FaUserPlus,
  FaChartLine,
  FaSitemap,
  FaSignOutAlt,
  FaCogs,
  FaSmileBeam,
  FaHeadphones,
  FaChevronDown,
  FaTimes,
  FaEllipsisH,
  FaChartBar,
  FaLightbulb,
  FaMapMarkedAlt,
  FaCalendarTimes,
  FaTrophy
} from 'react-icons/fa';
import defaultLogo from '../assets/logo.png';
import '../styles/Dashboard.css';
import MeuPainel from './MeuPainel';
import NotificationBell from './NotificationBell';
import ConfiguracoesPeople from './ConfiguracoesPeople';
import NotificationTemplateEditor from './ConfiguracoesNotificacoes';
import TermometroHumor from './Colaborador/TermometroHumor';
import MoodManagement from './Mood/MoodManagement'; // Importe o novo componente (ajuste o caminho)

// --- COMPONENTE RECURSIVO DE MENU ---
const MenuItem = ({ item, collapsed, activePath, handleMenuToggle, openMenu }) => {
  const hasSubItems = item.subItems && item.subItems.length > 0;
  const isSubMenuOpen = openMenu === item.key;

  // Verifica se o item atual ou algum subitem está ativo
  const isActive = hasSubItems 
    ? isSubMenuOpen || item.subItems.some(sub => activePath.startsWith(sub.basePath || sub.to))
    : activePath === item.to;

  if (hasSubItems) {
    return (
      <div className={`dashboard-menu-item-group ${isSubMenuOpen ? 'open' : ''}`}>
        <button onClick={() => handleMenuToggle(item.key)} className={`dashboard-menu-item ${isActive ? 'active' : ''}`}>
          {item.icon}
          {!collapsed && <span className="dashboard-menu-item-label">{item.label}</span>}
          {!collapsed && <FaChevronDown className="dropdown-icon" />}
        </button>
        {isSubMenuOpen && !collapsed && (
          <div className="submenu-container">
            {item.subItems.map(subItem => (
              <NavLink to={subItem.to} key={subItem.key} className={({ isActive }) => `dashboard-submenu-item${isActive ? ' active' : ''}`} title={subItem.label} end>
                {subItem.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.to} key={item.key} className={({ isActive }) => `dashboard-menu-item${isActive ? ' active' : ''}`} title={item.label} end={item.exact}>
      {item.icon}
      {!collapsed && <span className="dashboard-menu-item-label">{item.label}</span>}
    </NavLink>
  );
};

const Dashboard = ({ children }) => {
  // --- ESTADOS ---
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [canViewTeam, setCanViewTeam] = useState(false);
  const [visualSettings, setVisualSettings] = useState({
    logoUrl: defaultLogo,
    primaryColor: '#002454',
    secondaryColor: '#8dbefd'
  });

  const [showMoodSurveyModal, setShowMoodSurveyModal] = useState(false);
  const [activeMoodCampaign, setActiveMoodCampaign] = useState(null);
  const [currentTime, setCurrentTime] = useState(Timestamp.now());
  
  const [openMenu, setOpenMenu] = useState(null);
  
  const [mobileModalMenu, setMobileModalMenu] = useState({
    isOpen: false,
    items: [],
    label: ''
  });

  const [usageData, setUsageData] = useState({ used: 0, total: 10 });
  
  const navigate = useNavigate();
  const location = useLocation();

  const now = new Date();
  const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonthName = nextMonthDate.toLocaleString('pt-BR', { month: 'long' });
  const renewalDateString = `Renova em 1º de ${nextMonthName}`;

  // --- HANDLERS ---
  const handleMenuToggle = (key) => {
    setOpenMenu(openMenu === key ? null : key);
  };

   useEffect(() => {
    if (collapsed) {
      setOpenMenu(null);
    }
  }, [collapsed]);
  
  // Atualiza relógio para checagens periódicas
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Timestamp.now());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Monitora uso de créditos IA
  useEffect(() => {
    if (!userData?.empresa_id) return;
    const periodId = new Date().toISOString().slice(0, 7);
    const unsubUsage = onSnapshot(doc(db, 'Empresas', userData.empresa_id, 'analysisUsage', periodId), (doc) => {
        setUsageData({ total: 10, used: doc.exists() ? doc.data().count || 0 : 0 });
    });
    return () => unsubUsage();
  }, [userData]);


  const handleLogout = async () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      try {
        await auth.signOut();
        navigate('/');
      } catch (error) {
        console.error("Erro ao fazer logout:", error);
        alert("Não foi possível sair. Tente novamente.");
      }
    }
  };

  const handleMobileModalOpen = (menu) => {
    setMobileModalMenu({
      isOpen: true,
      items: menu.items,
      label: menu.label
    });
  };

  const handleMobileModalClose = () => {
    setMobileModalMenu({ isOpen: false, items: [], label: '' });
  };

  // Aplica cores personalizadas (White Label)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', visualSettings.primaryColor || '#002454');
    root.style.setProperty('--secondary-color', visualSettings.secondaryColor || '#8dbefd');
    root.style.setProperty('--primary-color-light', visualSettings.primaryColor ? `${visualSettings.primaryColor}20` : '#e0f2ff');
  }, [visualSettings]);

  // Monitora Campanhas de Pesquisa de Humor (Termômetro)
  useEffect(() => {
    if (!currentUser?.uid || !userData?.empresa_id) {
      return;
    }
    const campaignsRef = collection(db, 'Empresas', userData.empresa_id, 'moodSurveyCampaigns');
    const q = query(campaignsRef, where('scheduledFor', '<=', currentTime));
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const dueCampaigns = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        dueCampaigns.sort((a, b) => b.scheduledFor.toMillis() - a.scheduledFor.toMillis());
        const latestCampaign = dueCampaigns[0];
        const userPrivateDocRef = doc(db, 'usersprivado', currentUser.uid);
        const userPrivateDocSnap = await getDoc(userPrivateDocRef);
        const lastResponseId = userPrivateDocSnap.exists() ? userPrivateDocSnap.data().lastMoodSurveyResponseId : null;
        if (latestCampaign.id !== lastResponseId) {
          setActiveMoodCampaign(latestCampaign);
          setShowMoodSurveyModal(true);
        } else {
          setShowMoodSurveyModal(false);
        }
      } else {
        setShowMoodSurveyModal(false);
      }
    }, (err) => {
      console.error("Erro no listener da campanha de humor:", err);
    });
    return () => unsubscribe();
  }, [currentUser, userData, currentTime]);

  // --- LISTENER PRINCIPAL DE DADOS DO USUÁRIO ---
  useEffect(() => {
    let unsubscribeUserDoc = () => {}; 

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
        if (!user) {
            navigate('/login');
            setIsLoading(false);
            setUserData(null);
            setCurrentUser(null);
            unsubscribeUserDoc(); 
            return;
        }

        setIsLoading(true);
        setCurrentUser(user);
        
        const userDocRef = doc(db, 'users', user.uid);
        
        unsubscribeUserDoc = onSnapshot(userDocRef, async (userDocSnap) => {
            if (!userDocSnap.exists()) {
                setError("Dados de perfil não encontrados. Contate o suporte.");
                setIsLoading(false);
                setUserData(null);
                await auth.signOut();
                navigate('/login');
                return;
            }

            let fetchedUserData = { id: userDocSnap.id, ...userDocSnap.data() };
            
            // Sincroniza foto se necessário
            if (!fetchedUserData.photoURL && user.photoURL) {
                try {
                    const functions = getFunctions(getApp(), 'southamerica-east1');
                    const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
                    updateUserProfile().catch(console.error);
                    fetchedUserData.photoURL = user.photoURL;
                } catch (e) {}
            }

            if (fetchedUserData.empresa_id) {
                try {
                    const empresaDocRef = doc(db, 'Empresas', fetchedUserData.empresa_id);
                    const empresaDocSnap = await getDoc(empresaDocRef);
                    fetchedUserData.modulosAssinados = empresaDocSnap.exists() ? empresaDocSnap.data().modulosAssinados || [] : [];

                    const settingsDocRef = doc(db, 'Empresas', fetchedUserData.empresa_id, 'settings', 'onboarding');
                    const settingsDoc = await getDoc(settingsDocRef);
                    if (settingsDoc.exists() && settingsDoc.data().visuals) {
                        setVisualSettings(settingsDoc.data().visuals);
                    }

                    // --- LÓGICA DE PERMISSÃO DE EQUIPE (LIDERANÇA) ---
                    const orgNodesRef = collection(db, 'Empresas', fetchedUserData.empresa_id, 'organizationChartNodes');
                    
                    // 1. Verifica se o usuário tem permissão explicita no perfil para ver subordinados
                    const hasViewPermission = fetchedUserData.canViewSubordinates === true;
                    
                    let userCanViewTeam = false;

                    if (hasViewPermission) {
                        // 2. Se tem permissão, busca o nó dele para ver se existem nós filhos (subordinados)
                        const qLeader = query(orgNodesRef, where("userId", "==", user.uid));
                        const leaderNodeSnapshot = await getDocs(qLeader);
                        
                        if (!leaderNodeSnapshot.empty) {
                            const leaderNodeId = leaderNodeSnapshot.docs[0].id;
                            const qSubordinates = query(orgNodesRef, where("parentId", "==", leaderNodeId));
                            const subordinatesSnapshot = await getDocs(qSubordinates);
                            
                            if (!subordinatesSnapshot.empty) {
                                userCanViewTeam = true;
                            }
                        }
                    }
                    
                    setCanViewTeam(userCanViewTeam);
                    // ------------------------------------------------

                } catch (relatedDataError) {
                        console.error("Dashboard: Erro ao buscar dados da empresa:", relatedDataError);
                }
            }

            setUserData(fetchedUserData); 
            setError(null);
            setIsLoading(false);

        }, (err) => {
            console.error("Dashboard: Erro ao ouvir documento do usuário:", err);
            setError("Não foi possível carregar seus dados de perfil em tempo real.");
            setIsLoading(false);
        });
    });

    return () => {
        unsubscribeAuth();
        unsubscribeUserDoc();
    };
  }, [navigate]);

  const handleSettingsUpdate = (newVisuals) => {
    setVisualSettings(newVisuals);
  };

  const handleMoodSurveySubmit = async () => {
    if (!currentUser?.uid || !activeMoodCampaign?.id) return;
    try {
        const userPrivateDocRef = doc(db, 'usersprivado', currentUser.uid);
        await updateDoc(userPrivateDocRef, {
            lastMoodSurveyResponseId: activeMoodCampaign.id
        });
        setShowMoodSurveyModal(false);
        setActiveMoodCampaign(null);
    } catch (error) {
        console.error("Erro ao atualizar status da resposta de humor:", error);
        alert("Ocorreu um erro ao salvar sua resposta. Por favor, tente novamente.");
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f2f6fc' }}><p>Carregando dashboard...</p></div>;
  }
  if (error) {
     return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px', textAlign: 'center' }}><p style={{color: 'red', marginBottom: '20px'}}>{error}</p><button onClick={() => auth.signOut().then(() => navigate('/login'))} className="btn btn-primary">Voltar para Login</button></div>;
  }
  if (!userData && !isLoading) {
      navigate('/login');
      return null;
  }

  const { departamento, name, photoURL, empresa_id } = userData || {};

  // --- CONFIGURAÇÃO DO MENU ---
const allMenuItems = [
    { key: 'meu-painel', label: 'Início', icon: <FaTachometerAlt />, to: '/dashboard', exact: true },
    
    // --- GRUPO ANÁLISE DE PESSOAS ---
    { 
      key: 'analise-pessoas-group', 
      label: 'Análise de Pessoas', 
      icon: <FaChartBar />, 
      to: '/analise-pessoas', 
      basePath: '/analise-pessoas', 
      allowedDepartments: ['People'],
      subItems: [
        { 
            key: 'dash-pessoas', 
            label: 'Dash de Pessoas', 
            to: '/analise-pessoas', 
            allowedDepartments: ['People'] 
        },
        { 
            key: 'absenteismo', 
            label: 'Absenteísmo', 
            to: '/absenteismo', 
            allowedDepartments: ['People'] 
        },
        { 
            key: 'mapa-calor', 
            label: 'Geolocalização (Lojas)', 
            to: '/mapa-calor', 
            allowedDepartments: ['People'] 
        },
        { 
            key: 'gestao-humor', 
            label: 'Gestão de Humor', 
            to: '/gestao-humor', 
            allowedDepartments: ['People'] 
        }
      ]
    },
    // -------------------------------------------------------------

    { key: 'minha-equipe', label: 'Equipe', icon: <FaUsers />, to: '/minha-equipe', isVisible: canViewTeam },
    { key: 'avaliacoes', label: 'Avaliações', icon: <FaChartLine />, to: '/avaliacoes', basePath: '/avaliacoes', requiredModule: 'AVALIACOES', subItems: [
        { key: 'avaliacoes-ciclos', label: 'Gerenciar Ciclos', to: '/avaliacoes', allowedDepartments: ['People'] },
        { key: 'avaliacoes-progresso-lider', label: 'Progresso da Equipe', to: '/avaliacoes', isVisible: canViewTeam },
        { key: 'minhas-avaliacoes', label: 'Minhas Tarefas', to: '/avaliacoes/minhas-tarefas' },
        { key: 'meus-resultados', label: 'Meus Resultados', to: '/meus-resultados' }
    ]},{ 
    key: 'colaborador-mes', 
    label: 'Colaborador do Mês', 
    icon: <FaTrophy />, 
    to: '/colaborador-do-mes', 
    exact: false 
},
    { key: 'ouvidoria', label: 'Ouvidoria', icon: <FaHeadphones />, to: '/ouvidoria', requiredModule: 'OUVIDORIA' },
    { key: 'clientes', label: 'Clientes', icon: <FaUsers />, to: '/clientes', allowedDepartments: ['CS', 'Comercial', 'Financeiro'] },
    { key: 'faturas', label: 'Faturas', icon: <FaFileInvoice />, to: '/faturas', allowedDepartments: ['Financeiro'] },
    { key: 'cadastro', label: 'Cadastro', icon: <FaUserPlus />, to: '/cadastro', allowedDepartments: ['People'] },
    { key: 'organograma', label: 'Organograma', icon: <FaSitemap />, to: '/organograma' },
    { key: 'configuracoes', label: 'Configurações', icon: <FaCogs />, to: '/configuracoes-people', basePath: '/configuracoes-people', subItems: [
        { key: 'config-geral', label: 'Geral', to: '/configuracoes-people' },
        { key: 'config-notificacoes', label: 'Notificações', to: '/configuracoes-people/notificacoes' },
    ], allowedDepartments: ['People'] },
    { key: 'logout', label: 'Sair', icon: <FaSignOutAlt />, onClick: handleLogout },
  ];
  
// Filtra os itens do menu com base nas permissões e departamentos
const menuItems = allMenuItems.map(item => {
    // 1. Checa módulo contratado (se aplicável)
    const hasModuleAccess = !item.requiredModule || (userData?.modulosAssinados && userData.modulosAssinados.includes(item.requiredModule));
    if (!hasModuleAccess) {
        return null;
    }

    // 2. Checa departamento e visibilidade
    const isParentAllowedByDept = !item.allowedDepartments || item.allowedDepartments.includes(departamento);
    const isParentVisibleByFlag = item.isVisible === undefined || item.isVisible;

    if (!isParentAllowedByDept || !isParentVisibleByFlag) {
        return null;
    }

    // 3. Checa sub-itens
    if (item.subItems) {
        const visibleSubItems = item.subItems.filter(sub => {
            const isSubAllowedByDept = !sub.allowedDepartments || sub.allowedDepartments.includes(departamento);
            const isSubVisibleByFlag = sub.isVisible === undefined || sub.isVisible;
            
            if (sub.key === 'avaliacoes-progresso-lider') return canViewTeam;
            if (sub.key === 'avaliacoes-ciclos') return departamento === 'People';

            return isSubAllowedByDept && isSubVisibleByFlag;
        });

        // Remove duplicatas de URL se houver
        const uniqueSubItems = visibleSubItems.filter((sub, index, self) => index === self.findIndex((s) => s.to === sub.to));
        
        return uniqueSubItems.length > 0 ? { ...item, subItems: uniqueSubItems } : null;
    }

    return item;

}).filter(Boolean);

  // Divide itens para menu inferior mobile (Prioritários vs Mais)
  const primaryNavKeys = ['meu-painel', 'minha-equipe', 'avaliacoes', 'ouvidoria'];
  const primaryNavItems = menuItems.filter(item => primaryNavKeys.includes(item.key));
  const moreNavItems = menuItems.filter(item => !primaryNavKeys.includes(item.key));
  const isMoreMenuActive = moreNavItems.some(item => item.to && location.pathname.startsWith(item.to));
  
  // Variáveis para renderização condicional do conteúdo principal
  const isConfigPage = location.pathname === '/configuracoes-people';
  const isNotificationConfigPage = location.pathname === '/configuracoes-people/notificacoes';
  const isDashboardHomePage = location.pathname === '/dashboard' || location.pathname === '/';

  return (
    <div className={`dashboard-wrapper ${collapsed ? 'collapsed' : ''}`}>
        
        {/* Modal de Humor (Termômetro) */}
        {showMoodSurveyModal && (
            <div className="modal-backdrop mood-survey-modal-backdrop">
                <div className="modal-content mood-survey-modal-content">
                    <div className="modal-header"><h4><FaSmileBeam /> Como você se sente hoje?</h4></div>
                    <div className="modal-body">
                        <TermometroHumor 
                            isReadOnly={false} 
                            userId={currentUser?.uid} 
                            historicoHumor={[]} 
                            userData={userData} 
                            empresaId={empresa_id} 
                            isModalMode={true} 
                            onSurveySubmit={handleMoodSurveySubmit} 
                            campaignId={activeMoodCampaign?.id} 
                            hideTitle={true}
                        />
                    </div>
                </div>
            </div>
        )}

        {/* Modal Menu Mobile */}
        {mobileModalMenu.isOpen && (
            <div className="mobile-submenu-backdrop" onClick={handleMobileModalClose}>
                <div className="mobile-submenu-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="mobile-submenu-header">
                        <h3>{mobileModalMenu.label}</h3>
                        <button onClick={handleMobileModalClose} className="mobile-submenu-close-btn"><FaTimes /></button>
                    </div>
                    <div className="mobile-submenu-list">
                        {mobileModalMenu.items.map(item => { 
                            if (item.onClick) { 
                                return (<button key={`mobile-${item.key}`} onClick={() => { item.onClick(); handleMobileModalClose(); }} className="mobile-submenu-item mobile-submenu-action-item">{item.label}</button>); 
                            } 
                            return (<NavLink to={item.to} key={`mobile-${item.key}`} className={({ isActive }) => `mobile-submenu-item${isActive ? ' active' : ''}`} onClick={handleMobileModalClose}>{item.label}</NavLink>); 
                        })}
                    </div>
                </div>
            </div>
        )}

        {/* HEADER */}
        <header className="dashboard-header">
            <div className="dashboard-header-left">
                <div className="dashboard-header-logo">
                    <img src={visualSettings.logoUrl || defaultLogo} alt="Logo da Empresa" />
                </div>
                <FaBars className="dashboard-header-toggle" onClick={() => setCollapsed(!collapsed)} title={collapsed ? "Expandir menu" : "Recolher menu"} />
            </div>
            <div className="dashboard-header-user-info">
                <div className="dashboard-header-actions">
                    {userData && (
                        <div className="dashboard-header-credits" title={`Créditos de Análise IA. ${renewalDateString}`}>
                            <FaLightbulb />
                            <span className="credits-label">Créditos IA:</span>
                            <span className="credits-count">{`${usageData.used} / ${usageData.total}`}</span>
                        </div>
                    )}
                    {currentUser && empresa_id && ( <NotificationBell currentUserInfo={{ uid: currentUser.uid, empresaId: empresa_id, name: name }} /> )}
                </div>
                <div className="dashboard-header-profile">
                    {photoURL && photoURL.trim() !== "" ? ( 
                        <img src={photoURL} alt="Foto do perfil" className="dashboard-user-photo" onError={(e) => { e.target.src = defaultLogo; e.target.style.border = '1px solid #ccc'; }} /> 
                    ) : ( 
                        <div className="dashboard-user-photo-placeholder"><FaUsers/></div> 
                    )}
                    <span className="dashboard-user-name">{name || 'Usuário'}</span>
                </div>
            </div>
        </header>

        {/* CORPO */}
        <div className="dashboard-body">
            {/* SIDEBAR (Desktop) */}
            <aside className="dashboard-sidebar">
                <nav className="dashboard-sidebar-content">
                    {menuItems.filter(item => item.key !== 'logout').map(item => (
                        <MenuItem key={item.key} item={item} collapsed={collapsed} activePath={location.pathname} handleMenuToggle={handleMenuToggle} openMenu={openMenu}/>
                    ))}
                    <div className="dashboard-sidebar-bottom-nav">
                    {menuItems.filter(item => item.key === 'logout').map(item => (
                        <button key={item.key} onClick={item.onClick} className="dashboard-menu-item dashboard-menu-item-logout" title={item.label}>
                            {item.icon}
                            {!collapsed && <span className="dashboard-menu-item-label">{item.label}</span>}
                        </button>
                    ))}
                    </div>
                </nav>
                <div className="dashboard-sidebar-footer">
                    © {new Date().getFullYear()} Hub Gestão
                </div>
            </aside>

            {/* BOTTOM BAR (Mobile) */}
            <nav className="dashboard-bottom-bar">
                {primaryNavItems.map(item => {
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isActive = hasSubItems ? item.subItems.some(sub => location.pathname.startsWith(sub.basePath || sub.to)) : location.pathname === item.to;
                    if (hasSubItems) { 
                        return (<button key={`bottom-${item.key}`} onClick={() => handleMobileModalOpen({ label: item.label, items: item.subItems })} className={`bottom-bar-item${isActive ? ' active' : ''}`} title={item.label}>{item.icon}<span className="bottom-bar-label">{item.label}</span></button>); 
                    }
                    return (<NavLink to={item.to} key={`bottom-${item.key}`} className={({ isActive }) => `bottom-bar-item${isActive ? ' active' : ''}`} title={item.label} end={item.exact}>{item.icon}<span className="bottom-bar-label">{item.label}</span></NavLink>);
                })}
                {moreNavItems.length > 0 && (
                    <button onClick={() => handleMobileModalOpen({ label: 'Mais Opções', items: moreNavItems })} className={`bottom-bar-item${isMoreMenuActive ? ' active' : ''}`} title="Mais">
                        <FaEllipsisH /><span className="bottom-bar-label">Mais</span>
                    </button>
                )}
            </nav>

            {/* MAIN CONTENT */}
            <main className="dashboard-main">
                {(() => {
                    if (isConfigPage) { return <ConfiguracoesPeople userData={userData} onSettingsUpdate={handleSettingsUpdate} />; }
                    if (isNotificationConfigPage) { return <NotificationTemplateEditor empresaId={userData?.empresa_id} />; }
                    // Passa props globais para filhos (páginas)
                    if (children && !isDashboardHomePage) { return React.cloneElement(children, { userData, canViewTeam, usageData, visualSettings }); }
                    return <MeuPainel userData={userData} />;
                })()}
            </main>
        </div>
    </div>
  );
};

export default Dashboard;